import type { OpUnitType, QUnitType, UnitType } from "dayjs";

import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// 🚨 企業級鐵律：強制載入 UTC 與 Timezone 插件
dayjs.extend(duration);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(utc);
dayjs.extend(timezone);

type DateType = string | number | Date;

// 💡 解決方案：統一使用 UTC 進行內部運算，避免客戶端瀏覽器時區差異導致班機時間錯亂;
export const getUtcDayjs = (date?: DateType) => {
  return date ? dayjs.utc(date) : dayjs.utc();
};

export const isValidDate = (date: any) => {
  return dayjs(date).isValid();
};

export const formatDate = (date: DateType, formatStr?: string) => {
  if (!isValidDate(date)) return String(date);
  // 顯示時，將 UTC 時間轉換為適當的格式 (後續可擴充依據機場時區顯示)
  return getUtcDayjs(date).format(formatStr || "YYYY-MM-DD");
};

/**
 * 返回兩個日期時間之間的時長 (保證使用絕對時間點計算)
 */
export const getDuration = (
  date1: DateType,
  date2: DateType,
  unit: OpUnitType | QUnitType = "millisecond",
  isFloat: boolean = false,
) => {
  if (!isValidDate(date1) || !isValidDate(date2)) return 0;
  const d1 = getUtcDayjs(date1);
  const d2 = getUtcDayjs(date2);
  return d1.diff(d2, unit, isFloat);
};

export const toReadableDuration = (minutes: number) => {
  if (!minutes || minutes < 0) return "--";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

// --- 以下所有比較方法皆強制升級為 UTC 安全比較 ---
export const getValue = (date: DateType, unit: UnitType = "month") => {
  return isValidDate(date) ? getUtcDayjs(date).get(unit) : date;
};

export const isDatesBefore = (
  date1: DateType,
  date2: DateType,
  unit: UnitType = "millisecond",
) => {
  if (!isValidDate(date1) || !isValidDate(date2)) return false;
  return getUtcDayjs(date1).isBefore(getUtcDayjs(date2), unit);
};

export const isDatesSameOrBefore = (
  date1: DateType,
  date2: DateType,
  unit: UnitType = "millisecond",
) => {
  if (!isValidDate(date1) || !isValidDate(date2)) return false;
  return getUtcDayjs(date1).isSameOrBefore(getUtcDayjs(date2), unit);
};

export const isDatesAfter = (
  date1: DateType,
  date2: DateType,
  unit: UnitType = "millisecond",
) => {
  if (!isValidDate(date1) || !isValidDate(date2)) return false;
  return getUtcDayjs(date1).isAfter(getUtcDayjs(date2), unit);
};

export const isDatesSameOrAfter = (
  date1: DateType,
  date2: DateType,
  unit: UnitType = "millisecond",
) => {
  if (!isValidDate(date1) || !isValidDate(date2)) return false;
  return getUtcDayjs(date1).isSameOrAfter(getUtcDayjs(date2), unit);
};
